import React, { ReactNode } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { X } from "lucide-react";
import { cn } from "@/shared/lib/cn";

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  maxWidth?: string;
  noPadding?: boolean;
  clean?: boolean;
}

/**
 * Modal — construído sobre Radix Dialog.
 *
 * API preservada (isOpen/onClose/title/maxWidth/noPadding/clean) para
 * compatibilidade com todos os consumidores existentes.
 *
 * Acessibilidade entregue pelo Radix (sem código manual):
 *  · focus-trap + restauração de foco ao fechar
 *  · fecha no Esc e no clique fora
 *  · role="dialog", aria-modal, aria-labelledby (via Dialog.Title)
 *  · scroll-lock do body
 *  · animações via data-state (respeitam prefers-reduced-motion pelo globals.css)
 *
 * No mobile (< md) vira bottom sheet: ocupa a largura toda, encosta na borda
 * inferior, sobe/desce deslizando e respeita a barra de gestos do iPhone.
 */
export function Modal({
  isOpen,
  onClose,
  title,
  children,
  maxWidth = "max-w-5xl",
  noPadding = false,
  clean = false,
}: ModalProps) {
  return (
    <Dialog.Root open={isOpen} onOpenChange={(open) => { if (!open) onClose(); }}>
      <Dialog.Portal>
        <Dialog.Overlay
          className="fixed inset-0 z-modal bg-black/50 backdrop-blur-sm data-[state=open]:animate-overlay-enter data-[state=closed]:animate-overlay-exit"
        />
        <Dialog.Content
          aria-describedby={undefined}
          className={cn(
            "fixed left-1/2 top-1/2 z-modal -translate-x-1/2 -translate-y-1/2 w-[calc(100%-2rem)] max-h-[92vh] flex flex-col outline-none",
            "data-[state=open]:animate-modal-enter data-[state=closed]:animate-modal-exit",
            maxWidth,
            "max-md:inset-x-0 max-md:bottom-0 max-md:top-auto max-md:left-0 max-md:translate-x-0 max-md:translate-y-0 max-md:w-full max-md:max-w-none max-md:max-h-[92dvh]",
            "max-md:data-[state=open]:animate-sheetUp max-md:data-[state=closed]:animate-sheetDown",
            clean
              ? ""
              : "bg-white dark:bg-night_surface rounded-xl max-md:rounded-b-none shadow-lg ring-1 ring-black/[0.06] dark:ring-white/[0.08] overflow-hidden"
          )}
        >
          {clean ? (
            <Dialog.Title className="sr-only">{title || "Modal"}</Dialog.Title>
          ) : (
            <div className="shrink-0 bg-white dark:bg-night_surface border-t-[3px] border-dark_green px-6 pt-5 pb-4 flex justify-between items-center gap-4 border-b border-slate-100 dark:border-white/[0.06] z-20 max-md:px-4 max-md:pt-4 max-md:pb-3">
              <div className="flex items-center gap-3 min-w-0">
                <Dialog.Title className="text-slate-800 dark:text-zinc-300 font-semibold text-[17px] leading-snug tracking-tight truncate">
                  {title || "Detalhes"}
                </Dialog.Title>
              </div>
              <Dialog.Close asChild>
                <button
                  type="button"
                  aria-label="Fechar"
                  className="ds-touch-target shrink-0 text-slate-400 hover:text-slate-700 dark:text-zinc-500 dark:hover:text-zinc-200 hover:bg-slate-100 dark:hover:bg-night_input p-1.5 rounded-lg transition-colors"
                >
                  <X size={20} />
                </button>
              </Dialog.Close>
            </div>
          )}
          <div className={cn("flex-1 overflow-y-auto custom-scrollbar", clean || noPadding ? "" : "p-4 pb-[calc(1rem+var(--safe-bottom))] md:p-8")}>
            {children}
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
